import AddToQueueSharpIcon from '@mui/icons-material/AddToQueueSharp';
import ClearSharpIcon from '@mui/icons-material/ClearSharp';
import DoneSharpIcon from '@mui/icons-material/DoneSharp';
import DriveEta from '@mui/icons-material/DriveEta';
import EmojiPeople from '@mui/icons-material/EmojiPeople';
import LaptopSharpIcon from '@mui/icons-material/LaptopSharp';
import PersonAdd from '@mui/icons-material/PersonAdd';
import ThumbUp from '@mui/icons-material/ThumbUp';
import { Chip } from '@mui/material';
import { DateTime } from 'luxon';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { bookingsAPI } from '../../../api/bookingsAPI';
import {
    BookingAuditEntry,
    TimelineStory,
    VehicleAuditEntry,
} from '../../../api/models/Common/Timelines';
import Timeline from '../../../materialUI/components/Timeline/Timeline.js';
import DisplayDate from '../DisplayDates/DisplayDate';
import DisplayPrice from '../DisplayValues/DisplayPrice';

type TimelineProps = {
  bookingId: number
  fixedPriceDisplay: boolean
  displayVATPrice: boolean
  vehicleId?: number
}

const mapBookingAuditToArray = (
  bookingAudit: BookingAuditEntry,
): TimelineStory => {
  switch (bookingAudit.bookingStatusTypeName) {
    case 'Confirmed':
      return {
        // inverted: bookingAudit.lastUpdatedBy === 'SYSTEM' ? true : false,
        badgeColor: 'success',
        badgeIcon: DoneSharpIcon,
        title: 'Status: Confirmed',
        titleColor: 'success',
        body: (
          <div>
            <p>
              Your booking was accepted by the supplier. Check the vehicle audit
              for more information.
            </p>
          </div>
        ),
        footer: (
          <div style={{ fontSize: '8pt' }}>
            <p>
              Updated By: {bookingAudit.lastUpdatedBy}
              <br /> Updated Date:{' '}
              {moment(bookingAudit.lastUpdated).format('DD/MM/YY h:mm:ss a')}
            </p>
          </div>
        ),
      }
    case 'Cancelled':
      return {
        // inverted: bookingAudit.lastUpdatedBy === 'SYSTEM' ? true : false,
        badgeColor: 'danger',
        badgeIcon: ClearSharpIcon,
        title: 'Status: Cancelled',
        titleColor: 'danger',
        body: (
          <div>
            <p>
              Your booking has been successfully cancelled. All vehicles in this
              booking have also been cancelled.
            </p>
          </div>
        ),
        footer: (
          <div style={{ fontSize: '8pt' }}>
            <p>
              Updated By: {bookingAudit.lastUpdatedBy}
              <br /> Updated Date:{' '}
              {moment(bookingAudit.lastUpdated).format('DD/MM/YY h:mm:ss a')}
            </p>
          </div>
        ),
      }

    default:
      return {
        badgeColor: 'primary',
        badgeIcon: DoneSharpIcon,
        title: 'Status: Booked',
        titleColor: 'primary',
        body: (
          <div>
            <p>
              Booking Reference: {bookingAudit.bookingReference}
              <br />
              {/* When: {moment(bookingAudit.when).format('DD/MM/YY h:mm:ss a')} */}
              When:{' '}
              {DisplayDate({
                date: bookingAudit.when,
                timeZone: bookingAudit.timeZone,
                viewType: 'actual',
              })}
              <br />
              PickUp Location: {bookingAudit.pickup.addressLine1},{' '}
              {bookingAudit.pickup.town}
              <br />
              Drop Off Location: {bookingAudit.dropOff.addressLine1},{' '}
              {bookingAudit.dropOff.town}
              <br />
            </p>
          </div>
        ),
        footer: (
          <div style={{ fontSize: '8pt' }}>
            <p>
              Updated By: {bookingAudit.lastUpdatedBy}
              <br />
              Update Date:{' '}
              {moment(bookingAudit.lastUpdated).format('DD/MM/YY h:mm:ss a')}
            </p>
          </div>
        ),
      }
  }
}

const getBookingAuditData = (
  bookingAudits: Array<BookingAuditEntry> | undefined,
) => {
  const data: Array<TimelineStory> = []
  let inveertedValue = false
  if (bookingAudits) {
    bookingAudits.forEach((bookingAudit) => {
      const mapped = mapBookingAuditToArray(bookingAudit)
      mapped.inverted = inveertedValue
      data.push(mapped)
      inveertedValue = !inveertedValue
    })
  }
  return data
}

const TzChip = ({
  label,
  highlight,
}: {
  label: string
  highlight: boolean
}) => (
  <Chip
    label={label}
    size="small"
    color={highlight ? 'primary' : 'default'}
    sx={{
      height: 16,
      fontSize: '8pt',
      ml: 0.5,
      '& .MuiChip-label': { px: 0.75 },
    }}
  />
)

const formatInBookingTz = (date: Date, timeZone?: string) => {
  const dt = DateTime.fromISO(moment(date).toISOString()).setLocale('en-GB')
  const fmt = 'dd/MM/yy h:mm:ss a'
  const local = dt.toLocal()
  const effectiveZone = timeZone ?? 'UTC'
  const zoned = dt.setZone(effectiveZone)
  const differsFromLocal = effectiveZone !== local.zoneName
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {zoned.toFormat(fmt)}
      <TzChip
        label={(zoned.zoneName ?? effectiveZone).replace(/_/g, ' ')}
        highlight={differsFromLocal}
      />
    </span>
  )
}

const renderVehicleAuditFooter = (vehicleAudit: VehicleAuditEntry) => {
  const updatedDate = formatInBookingTz(
    vehicleAudit.when,
    vehicleAudit.timeZone,
  )
  return (
    <div style={{ fontSize: '8pt' }}>
      <p>
        {vehicleAudit.updatedBy && (
          <>
            Updated By: {vehicleAudit.updatedBy.name}
            <br />
          </>
        )}
        Updated Date: {updatedDate}
      </p>
    </div>
  )
}

const mapVehicleAuditToArray = (
  vehicleAudit: VehicleAuditEntry,
  fixedPriceDisplay: boolean,
  displayVATPrice: boolean,
): TimelineStory => {
  const footer = renderVehicleAuditFooter(vehicleAudit)
  console.log(vehicleAudit)
  switch (vehicleAudit.status) {
    case 'Confirmed':
      return {
        badgeColor: 'success',
        badgeIcon: DoneSharpIcon,
        title: 'Vehicle Confirmed',
        titleColor: 'success',
        body: (
          <div>
            <p>
              Your booking for{' '}
              {vehicleAudit.vehicleDetails.displayName
                ? vehicleAudit.vehicleDetails.displayName
                : 'TBC'}{' '}
              vehicle has accepted by the supplier. <br /> <br />
              The supplier reference for this vehicle is{' '}
              {vehicleAudit.supplier.supplierReference}.
            </p>
          </div>
        ),
        footer,
      }
    case 'Cancelled':
      return {
        badgeColor: 'danger',
        badgeIcon: ClearSharpIcon,
        title: 'Vehicle Cancelled',
        titleColor: 'danger',
        body: (
          <div>
            <p>
              Your booking has been successfully cancelled. This vehicle has
              been cancelled with {vehicleAudit.supplier.companyName}.
            </p>
          </div>
        ),
        footer,
      }
    case 'Completed':
      return {
        badgeColor: 'success',
        badgeIcon: ThumbUp,
        title: 'Journey Completed',
        titleColor: 'success',
        body: (
          <div>
            <p>
              Your booking has been completed successfully. This journey has
              been completed with {vehicleAudit.supplier.companyName} for a
              price of{' '}
              {DisplayPrice({
                VATTotalPrice: vehicleAudit.actualPricing,
                priceType: 'exact',
                displayVATPrice: displayVATPrice,
              })}
              .
              <br />
              <br />
              Mileage: {vehicleAudit.journeyDistance} miles
              <br />
              CO2 Emmissions: {Math.round(vehicleAudit.co2Emmissions)}g
            </p>
          </div>
        ),
        footer,
      }
    case 'Booking_Update_Pending':
      return {
        badgeColor: 'primary',
        badgeIcon: ThumbUp,
        title: 'Booking Update (Pending)',
        titleColor: 'primary',
        body: (
          <div>
            <p>
              This jobs has been requested to be amended, with supplier{' '}
              {vehicleAudit.supplier.companyName} for a price of{' '}
              {DisplayPrice({
                VATTotalPrice: vehicleAudit.actualPricing,
                priceType: 'exact',
                displayVATPrice: displayVATPrice,
              })}
            </p>
          </div>
        ),
        footer,
      }
    case 'Manual_Reallocation_Pending':
      return {
        badgeColor: 'primary',
        badgeIcon: AddToQueueSharpIcon,
        title: 'Manual Reallocation Pending',
        titleColor: 'primary',
        body: (
          <div>
            <p>
              This job is awaiting manual reallocation to a new supplier.
              <br />
              <br />
              Current Supplier: {vehicleAudit.supplier.companyName}
              <br />
              Supplier Reference: {vehicleAudit.supplier.supplierReference}
            </p>
          </div>
        ),
        footer,
      }
    case 'OnRoute':
      return {
        badgeColor: 'success',
        badgeIcon: DriveEta,
        title: 'Driver On Route',
        titleColor: 'success',
        body: (
          <div>
            <p>
              The below driver was dispatched to your location:
              <br />
              <br />
              Driver Name: {vehicleAudit.driver.name}
              <br />
              Registration No.: {vehicleAudit.driver.vehicle.registrationNumber}
              <br />
              Make and Model: {vehicleAudit.driver.vehicle.makeAndModel}
              <br />
              Colour: {vehicleAudit.driver.vehicle.colour}
            </p>
          </div>
        ),
        footer,
      }
    case 'Arrived':
      return {
        badgeColor: 'success',
        badgeIcon: EmojiPeople,
        title: 'Driver Arrived',
        titleColor: 'success',
        body: (
          <div>
            <p>Driver arrived at pickup location</p>
          </div>
        ),
        footer,
      }
    case 'Supplier_Cancelled':
      return {
        badgeColor: 'danger',
        badgeIcon: EmojiPeople,
        title: 'Supplier Cancelled Booking',
        titleColor: 'danger',
        body: (
          <div>
            <p>The supplier has cancelled the booking.</p>
          </div>
        ),
        footer,
      }
    case 'No_Show':
      return {
        badgeColor: 'danger',
        badgeIcon: EmojiPeople,
        title: 'Supplier Cancelled Booking',
        titleColor: 'danger',
        body: (
          <div>
            <p>The driver registered this trip as a no show.</p>
          </div>
        ),
        footer,
      }
    case 'Passenger_Picked_Up':
      return {
        badgeColor: 'primary',
        badgeIcon: PersonAdd,
        title: 'Passenger Picked Up',
        titleColor: 'primary',
        body: (
          <div>
            <p>Passenger picked up</p>
          </div>
        ),
        footer,
      }
    case 'Passenger_Dropped_Off':
      return {
        badgeColor: 'primary',
        badgeIcon: EmojiPeople,
        title: 'Passenger Dropped Off',
        titleColor: 'primary',
        body: (
          <div>
            <p>
              Passenger dropped off ({vehicleAudit.where.latitude},
              {vehicleAudit.where.longitude})
            </p>
          </div>
        ),
        footer,
      }
    case 'OnRoute_Passenger_On_Board':
      return {
        badgeColor: 'primary',
        badgeIcon: DriveEta,
        title: 'Driver On Route Passenger On Board',
        titleColor: 'primary',
        body: (
          <div>
            <p>On route with passenger onboard</p>
          </div>
        ),
        footer,
      }
    case 'Allocated':
      return {
        badgeColor: 'success',
        badgeIcon: ThumbUp,
        title: 'Supplier Allocated',
        titleColor: 'success',
        body: (
          <div>
            <p>
              <strong>The Jyrney system allocated the below supplier:</strong>
              <br />
              Vehicle Type:{' '}
              {vehicleAudit.vehicleDetails.displayName
                ? vehicleAudit.vehicleDetails.displayName
                : 'TBC'}
              <br />
              Supplier Name: {vehicleAudit.supplier.companyName}
              <br />
              Job Reference: {vehicleAudit.jobReference}
              <br />
              Estimate Price:{' '}
              {DisplayPrice({
                VATTotalPrice: vehicleAudit.estimatedPricing,
                priceType: fixedPriceDisplay ? 'exact' : 'range',
                displayVATPrice: displayVATPrice,
              })}
            </p>
          </div>
        ),
        footer,
      }
    case 'Driver_Allocated':
      return {
        badgeColor: 'primary',
        badgeIcon: AddToQueueSharpIcon,
        title: 'Driver Allocated',
        titleColor: 'primary',
        body: (
          <div>
            <p>
              You driver has been allocated:
              <br />
              <br />
              Driver Name: {vehicleAudit.driver.name}
              <br />
              Vehicle Details: {vehicleAudit.driver.vehicle.makeAndModel}
              <br />
              Registration Plate:{' '}
              {vehicleAudit.driver.vehicle.registrationNumber}
            </p>
          </div>
        ),
        footer,
      }
    case 'Completed_Pending_Invoice':
      return {
        badgeColor: 'success',
        badgeIcon: AddToQueueSharpIcon,
        title: 'Completed Pending Invoice',
        titleColor: 'success',
        body: (
          <div>
            <p>
              Your booking has completed. We are waiting on the supplier to send
              us the invoice information for this trip.
            </p>
          </div>
        ),
        footer,
      }
    case 'Declined':
      return {
        badgeColor: 'danger',
        badgeIcon: AddToQueueSharpIcon,
        title: 'Declined by Supplier',
        titleColor: 'danger',
        body: (
          <div>
            <p>{vehicleAudit.supplier.companyName} declined this booking.</p>
          </div>
        ),
        footer,
      }
    default:
      return {
        badgeColor: 'primary',
        badgeIcon: LaptopSharpIcon,
        title: 'System Update',
        titleColor: 'primary',
        body: (
          <pre>
            <code>{JSON.stringify(vehicleAudit.status, null, 2)}</code>
          </pre>
        ),
        footer,
      }
  }
}

const getVehicleAuditData = (
  vehicleAudits: Array<VehicleAuditEntry> | undefined,
  fixedPriceDisplay: boolean,
  displayVATPrice: boolean,
) => {
  const data: Array<TimelineStory> = []
  let inveertedValue = false
  if (vehicleAudits) {
    console.log('vehicleAudits: ', vehicleAudits)
    vehicleAudits.forEach((vehicleAudit) => {
      if (vehicleAudit.status != 'Driver_Location_Update') {
        const mapped = mapVehicleAuditToArray(
          vehicleAudit,
          fixedPriceDisplay,
          displayVATPrice,
        )
        mapped.inverted = inveertedValue
        data.push(mapped)
        inveertedValue = !inveertedValue
      }
    })
  }
  return data
}

const TimelinePage = ({
  bookingId,
  fixedPriceDisplay,
  displayVATPrice,
  vehicleId,
}: TimelineProps) => {
  const bookingAuditData = useQuery({
    queryKey: ['fetch booking audit data'],
    queryFn: async () => bookingsAPI.bookingAudit(bookingId),
    enabled: !!vehicleId,
  })
  const vehicleAuditData = useQuery({
    queryKey: ['fetch vehicle audit data'],
    queryFn: async () => bookingsAPI.vehicleAudit(bookingId, vehicleId),
    enabled: !!vehicleId,
  })
  //check vehicle or booking
  console.log('bookingAuditData', bookingAuditData)
  //booking
  const stories: any = []
  if (vehicleId) {
    //Handle the Booking Audits
    //Process Data into needed array of stories
    const vehicleAuditDataTable = getVehicleAuditData(
      vehicleAuditData.data?.data.value,
      fixedPriceDisplay,
      displayVATPrice,
    )
    //Pass the array into the stories for timeline
    vehicleAuditDataTable.forEach((item) => stories.push(item))
  } else {
    //Handle the Booking Audits
    //Process Data into needed array of stories
    const bookingsAuditDataTable = getBookingAuditData(
      bookingAuditData.data?.data.value,
    )
    //Pass the array into the stories for timeline
    bookingsAuditDataTable.forEach((item) => stories.push(item))
  }

  return <Timeline stories={stories} />
}

export default TimelinePage
